import { Controller } from "@hotwired/stimulus"

// Removes its element when something asks it to.
//
// Optionally REMEMBERS that it was removed: give it a `key` and the dismissal
// is written to localStorage, so the element stays gone on the next visit and
// never renders in the first place. Without a key the behaviour is what it has
// always been — remove now, come back next render — which is what the flash
// alerts want.
//
// The key carries its own scope. The recap teaser passes one that contains the
// month, so a teaser waved away in August returns in September with nothing
// having to reset it.
//
// Optionally LEAVES rather than vanishes: `exit: "farewell"` plays the
// hand-off the streak card used to play when it gave its slot to the goal strip
// (the retired streak-farewell swap) before removing the element.
// Opt-in, because the flash alerts want the instant removal they have always
// had — an alert that takes a second to leave is an alert in the way.
// Optionally ARMED: with `arm_key`, any control marked `data-dismissible-armed`
// stays hidden until that key is set in localStorage by something else. The
// recap's last-day strip uses it so the X only appears once the deck has been
// opened — before that there is nothing to dismiss yet, only something to miss.
export default class extends Controller {
  static values = { key: String, exit: String, armKey: String }

  // Both curves come from the retired streak-farewell swap. The
  // DURATIONS deliberately do not: there, the resize outlasts the fade by
  // ~480ms because the goal strip is arriving into the space being cleared, so
  // the tail is covered by something worth watching. Nothing arrives here, so
  // the same tail is an empty gap still closing after the card has already
  // gone — it reads as lag rather than polish. One duration for both legs
  // keeps the card and its space landing on the same beat.
  static DURATION = 620
  static FADE_EASE = "cubic-bezier(0.32, 0, 0.3, 1)"
  static RESIZE_EASE = "cubic-bezier(0.22, 1, 0.36, 1)"

  // Sideways (a strip inside .strip-rail) the same farewell is shorter: the
  // neighbour slides in behind the card as it closes, so there is something to
  // watch and no reason to hold the beat for 620ms.
  static DURATION_X = 420
  // .strip-rail's gap. The card has to take its gap with it or the last frame
  // of the collapse still owes the row 8px, which the remove() then pays in
  // one jump.
  static RAIL_GAP_PX = 8

  // Every element sharing a key is one announcement, however many copies of it
  // the page happens to hold.
  static CHANNEL = "dismissible:dismissed"

  connect() {
    if (this.#remembered()) {
      this.element.remove()
      return
    }
    this.#syncArmed()
    this.#listen()
  }

  disconnect() {
    if (this.onPeerDismissed) {
      document.removeEventListener(this.constructor.CHANNEL, this.onPeerDismissed)
    }
  }

  // The dashboard renders one budget_content_panel PER BUDGET, so a strip on a
  // three-budget account exists three times over — same key, three elements,
  // only one of them on screen. Dismissing the visible one used to leave the
  // other two in the carousel, and scrolling sideways brought the card straight
  // back; it only stayed gone after a reload, when connect() read the key and
  // removed all three.
  //
  // So the dismissal is announced instead of kept private. Peers holding the
  // same key drop out immediately and without the farewell — they are off
  // screen, and animating something nobody can see only risks it being
  // mid-flight when the carousel arrives.
  #listen() {
    if (!this.hasKeyValue || !this.keyValue) return

    this.onPeerDismissed = (event) => {
      if (event.detail?.key !== this.keyValue) return
      if (event.detail?.source === this.element) return
      this.element.remove()
    }
    document.addEventListener(this.constructor.CHANNEL, this.onPeerDismissed)
  }

  // Reveal the armed controls, if any, once the arming key exists.
  #syncArmed() {
    if (!this.hasArmKeyValue || !this.armKeyValue) return
    if (!this.#stored(this.armKeyValue)) return

    this.element.querySelectorAll("[data-dismissible-armed]").forEach((el) => {
      el.hidden = false
    })
  }

  dismiss() {
    this.#remember()
    this.#announce()
    if (this.exitValue !== "farewell") { this.element.remove(); return }
    // Same farewell, whichever way the row runs: the rail collapses a width,
    // the stacked strips collapse a height.
    if (this.#inRail) this.#farewellX()
    else this.#farewell()
  }

  #announce() {
    if (!this.hasKeyValue || !this.keyValue) return

    document.dispatchEvent(new CustomEvent(this.constructor.CHANNEL, {
      detail: { key: this.keyValue, source: this.element }
    }))
  }

  // Cross-fade out while the element closes its own space. The element owns its
  // top margin (the dashboard strip is handed `mt-2.5` through `extra_class`),
  // so the margin animates to zero alongside the height — otherwise the row
  // collapses onto a leftover gap instead of onto nothing.
  #farewell() {
    const el = this.element

    if (this.#reducedMotion) {
      // Same fallback shape the streak-farewell swap used: no movement, just a soft fade.
      const fade = el.animate([ { opacity: 1 }, { opacity: 0 } ],
                              { duration: 300, easing: "ease-out", fill: "forwards" })
      fade.onfinish = () => el.remove()
      return
    }

    const height = el.getBoundingClientRect().height
    const cs = getComputedStyle(el)
    const box = {
      marginTop: cs.marginTop,
      marginBottom: cs.marginBottom,
      paddingTop: cs.paddingTop,
      paddingBottom: cs.paddingBottom,
      borderTopWidth: cs.borderTopWidth,
      borderBottomWidth: cs.borderBottomWidth
    }

    // Clipped so the mascot's overhang disappears with the box rather than
    // hanging in the gap as it closes.
    el.style.overflow = "hidden"
    // A caller's own floor would clamp the collapse and leave that many pixels
    // to drop in a single frame on remove(), which is the snap this is here to
    // avoid. Nothing sets one today; it costs one line to stay true anyway.
    el.style.minHeight = "0px"
    el.style.willChange = "opacity, transform, filter, height"

    const duration = this.constructor.DURATION

    el.animate(
      [
        { opacity: 1, transform: "scale(1) translateY(0)",      filter: "blur(0px)" },
        { opacity: 0, transform: "scale(0.97) translateY(3px)", filter: "blur(2px)" }
      ],
      { duration, easing: this.constructor.FADE_EASE, fill: "forwards" }
    )

    // Every part of the box that occupies vertical space goes to zero together,
    // so by the last frame the element already contributes exactly nothing to
    // the layout. Removing it then cannot move what is underneath — the same
    // guarantee swapStreakCard buys by landing the streak box on the incoming
    // strip's margin-box before it drops out of the grid.
    const resize = el.animate(
      [
        { height: `${height}px`, ...box },
        { height: "0px", marginTop: "0px", marginBottom: "0px",
          paddingTop: "0px", paddingBottom: "0px",
          borderTopWidth: "0px", borderBottomWidth: "0px" }
      ],
      { duration, easing: this.constructor.RESIZE_EASE, fill: "forwards" }
    )
    resize.onfinish = () => el.remove()
  }

  // Horizontal twin of #farewell, for a strip in the dashboard's rail
  // (budgets/_budget_content_panel). Same two legs, same curves: the card
  // fades and eases back while its own width closes, and the rail carries the
  // cards behind it into the space. Nothing sweeps and nothing bounces.
  //
  // The grow to full width, when this leaves one card standing, is not here:
  // strip_rail_controller sees the removal and .strip-rail's own flex-basis
  // transition plays it, after this animation rather than under it.
  #farewellX() {
    const el = this.element

    if (this.#reducedMotion) {
      const fade = el.animate([ { opacity: 1 }, { opacity: 0 } ],
                              { duration: 300, easing: "ease-out", fill: "forwards" })
      fade.onfinish = () => el.remove()
      return
    }

    // Measured, not the 80% the rail declares: a percentage basis animates
    // against a content box that is about to change under it.
    const width = el.getBoundingClientRect().width
    // Clipped so the mascot's overhang goes with the box rather than hanging
    // in the closing gap.
    el.style.overflow = "hidden"
    el.style.willChange = "opacity, transform, flex-basis"

    const duration = this.constructor.DURATION_X

    el.animate(
      [
        { opacity: 1, transform: "scale(1)" },
        { opacity: 0, transform: "scale(0.97)" }
      ],
      { duration, easing: this.constructor.FADE_EASE, fill: "forwards" }
    )

    const collapse = el.animate(
      [
        { flexBasis: `${width}px`, marginInlineEnd: "0px" },
        { flexBasis: "0px", marginInlineEnd: `-${this.constructor.RAIL_GAP_PX}px` }
      ],
      { duration, easing: this.constructor.RESIZE_EASE, fill: "forwards" }
    )
    collapse.onfinish = () => el.remove()
  }

  get #inRail() {
    return this.element.parentElement?.classList.contains("strip-rail") === true
  }

  get #reducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }

  #remembered() {
    if (!this.hasKeyValue || !this.keyValue) return false
    return this.#stored(this.keyValue)
  }

  #stored(key) {
    try {
      return window.localStorage.getItem(key) === "1"
    } catch {
      // Private mode and blocked storage both throw on access. A teaser that
      // cannot remember its dismissal is a much smaller problem than a crash
      // on connect, so treat it as never dismissed — and an X that never
      // arms is better than one that arms on a screen it should not.
      return false
    }
  }

  #remember() {
    if (!this.hasKeyValue || !this.keyValue) return
    try {
      window.localStorage.setItem(this.keyValue, "1")
    } catch {
      // Same as above: nothing to do, and nothing worth breaking over.
    }
  }
}
